// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import {
  Transfer,
  TransferRequest,
} from '@daml.js/utility-registry-v0/lib/Utility/Registry/V0/Holding/Transfer';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useAcceptTransferRequest } from '../../../hooks/mutations/registry/transfer/useAcceptTransferRequest';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  request: { contractId: string; state: string; payload: Transfer };
  disableDialog?: boolean;
};

export const AcceptTransferRequestDialog = ({
  disableDialog,
  open,
  onClose,
  request,
}: DialogProps) => {
  const [registrar, setRegistrar] = useState<string>('');
  const [senderLabel, setSenderLabel] = useState<string>('');

  const acceptTransferRequest = useAcceptTransferRequest();

  const resetState = useCallback(() => {
    setRegistrar('');
    setSenderLabel('');
  }, [setRegistrar, setSenderLabel]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    await acceptTransferRequest({
      cid: request.contractId as ContractId<TransferRequest>,
      argument: { senderLabel },
    });
    handleClose();
  }, [senderLabel, request.contractId, acceptTransferRequest, handleClose]);

  const disableSubmit = !registrar;
  return (
    <FormDialog
      open={open}
      title="Accept Transfer Request"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Accept"
      disableDialog={disableDialog}
      isSubmitDisabled={disableSubmit}
    >
      <TextInput label="Registrar" value={registrar} setValue={setRegistrar} required />
      <TextInput label="Label" value={senderLabel} setValue={setSenderLabel} hidden />
    </FormDialog>
  );
};
